import { httpApi } from '@api/http.api';
import { CONTENT_TYPE_FORM_DATA_HEADER, NO_REQUEST_TIMEOUT } from '@constants/api';
import type {
    CommonTablePaginatedResponse,
    CommonTableRequest,
} from '@routes/sidebar/admin-center/___shared___/types.ts';
import type { CSV_TYPE } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/constants.ts';
import type { CsvUploadStateResponse as SharedCsvUploadStateResponse } from '@routes/sidebar/admin-center/tenant-client/___pages___/configuration/___shared___/types.ts';
import type { AxiosResponse } from 'axios';

export type ShoreUsersRequest = CommonTableRequest;

export interface ShoreUser {
    id: number;
    employeeId: string | null;
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
    nationalityId: number | null;
    nationalityName: string | null;
    genderId: number | null;
    genderName: string | null;
    primaryEmail: string | null;
    secondaryEmail: string | null;
    phone: string | null;
    titleId: number | null;
    titleName: string | null;
    titleSince: string | null;
    shoreTeamId: number | null;
    shoreTeamName: string | null;
    reportsToId: number | null;
    reportsToName: string | null;
    isVesselSuperintendent: boolean;
    joinedSince: string | null;
    lastActivityUTC: string | null;
    lastChanged: string | null;
    isActive: boolean;
    isBuiltIn: boolean;
    createdAtUTC: string | null;
    lastLoginUTC: string | null;
}

export interface ShoreUsersResultsDescription {
    [k: string]: unknown;
}

export interface CsvUploadStateResponse extends SharedCsvUploadStateResponse {
    csvType: typeof CSV_TYPE.SHORE_USER;
}

export interface ShoreUsersResponse {
    data: ShoreUser[];
    pagination: CommonTablePaginatedResponse;
    resultsDescription: ShoreUsersResultsDescription;
}

export interface ShoreUserPayload {
    employeeId: string | null;
    firstName: string | null;
    lastName: string | null;
    dateOfBirth: string | null;
    nationalityId: number;
    genderId: number;
    primaryEmail: string | null;
    secondaryEmail: string | null;
    phone: string | null;
    titleId: number;
    titleSince: string | null;
    shoreTeamId: number;
    reportsToId: number;
    isVesselSuperintendent: boolean;
    joinedSince: string | null;
    isActive: boolean;
}

export type CreateShoreUserRequest = ShoreUserPayload;
export type UpdateShoreUserRequest = ShoreUserPayload;

export const getApiShoreUsers = (
    clientId: number,
    tenantAlias: string,
    payload: ShoreUsersRequest,
): Promise<ShoreUsersResponse> =>
    httpApi
        .post<ShoreUsersResponse>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/shore-users/search`, payload)
        .then(({ data }) => data);

export const createApiShoreUser = (
    clientId: number,
    tenantAlias: string,
    payload: CreateShoreUserRequest,
): Promise<ShoreUser> =>
    httpApi
        .post<ShoreUser>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/shore-users`, payload)
        .then(({ data }) => data);

export const updateApiShoreUser = (
    clientId: number,
    tenantAlias: string,
    id: number,
    payload: UpdateShoreUserRequest,
): Promise<ShoreUser> =>
    httpApi
        .put<ShoreUser>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/shore-users/${id}`, payload)
        .then(({ data }) => data);

export const deleteApiShoreUser = (clientId: number, tenantAlias: string, id: number): Promise<ShoreUser> =>
    httpApi
        .delete<ShoreUser>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/shore-users/${id}`)
        .then(({ data }) => data);

export const uploadApiShoreUsersCsv = (clientId: number, tenantAlias: string, formData: FormData): Promise<string> =>
    httpApi
        .post(`admin-center/clients/${clientId}/tenants/${tenantAlias}/upload-csv`, formData, {
            headers: CONTENT_TYPE_FORM_DATA_HEADER,
            timeout: NO_REQUEST_TIMEOUT,
        })
        .then(({ data }) => data);

export const getApiShoreUsersCsvUpload = (
    clientId: number,
    tenantAlias: string,
    id: string,
): Promise<CsvUploadStateResponse> =>
    httpApi
        .get<CsvUploadStateResponse>(`admin-center/clients/${clientId}/tenants/${tenantAlias}/upload-csv/${id}`)
        .then(({ data }) => data);

export const downloadApiShoreUsersCsv = (clientId: number, tenantAlias: string): Promise<AxiosResponse<Blob>> =>
    httpApi
        .get(`admin-center/clients/${clientId}/tenants/${tenantAlias}/download-csv`, { responseType: 'blob' })
        .then((res) => res);

export const downloadApiShoreUsersExampleCsv = (clientId: number, tenantAlias: string): Promise<AxiosResponse<Blob>> =>
    httpApi
        .get(`admin-center/clients/${clientId}/tenants/${tenantAlias}/example-csv`, { responseType: 'blob' })
        .then((res) => res);
